import { useRef } from 'react';
import type { ClipboardEvent, FormEvent, ReactNode } from 'react';

import { BrandMark } from '@/components/BrandMark';
import { Switch } from '@/components/Switch';

/**
 * 로그인 카드 — 바탕 위에 서는 <b>흰 판 한 장</b>.
 *
 * <h2>왜 화면에서 떼어 냈나</h2>
 * 환경설정 › 로그인 화면의 미리보기가 <b>카드까지</b> 보여야 하기 때문이다(사용자 지시
 * 2026-09-15). 시안을 고르는 일은 「사진이 예쁜가」 가 아니라 <b>「이 바탕 위에서 아이디 칸이
 * 읽히는가」</b> 라, 카드 없는 미리보기로는 정작 정해야 할 것을 못 정한다.
 *
 * <p>미리보기용 모형을 따로 그리지 않는다. 모형은 반드시 낡는다 — 카드에 칸이 하나 늘면
 * 미리보기만 옛 모습으로 남고, 그 사실은 아무도 모른다. <b>같은 조각</b>이 두 곳에 선다.
 *
 * <h2>상태를 들고 있지 않는다</h2>
 * 값과 손잡이는 전부 밖에서 온다. 여기에 상태를 두면 「비밀번호 저장」 해제 같은 일이 두 곳
 * (카드와 화면)에서 갈라지고, 봉인을 지우는 시점이 화면 밖으로 새어 나간다.
 */

/**
 * 카드의 최대 폭. <b>히어로를 되살릴 때 구체의 지름</b>이기도 하다 — 왼쪽 구체와 오른쪽 카드가
 * 같은 크기로 마주 서야 두 덩어리가 한 쌍으로 읽힌다(`LoginPage` 의 히어로 주석).
 */
export const CARD_WIDTH = 420;

type LoginCardProps = {
  /** 어두운 바탕(시안) 위에 서는가. 테두리와 그림자가 갈린다. */
  elevated: boolean;
  email: string;
  onEmailChange: (next: string) => void;
  password: string;
  onPasswordChange: (next: string) => void;
  /** 비밀번호 칸의 복사·잘라내기. 원문 대신 가짜 값이 클립보드에 들어간다. */
  onPasswordClipboard: (event: ClipboardEvent<HTMLInputElement>) => void;
  rememberEmail: boolean;
  onRememberEmailChange: (next: boolean) => void;
  rememberPassword: boolean;
  onRememberPasswordChange: (next: boolean) => void;
  pending: boolean;
  onSubmit: (event: FormEvent) => void;
  onResetRequest: () => void;
  /** 카드 아래에 붙는 안내(로그인 실패·초기화 요청 접수). 없으면 자리도 비지 않는다. */
  notices?: ReactNode;
  /** 「디자인아트」 스위치 — 로고 줄 오른쪽 끝. 손잡이를 안 주면(설정 미리보기) 스위치를 두지 않는다. */
  designArt?: boolean;
  onDesignArtChange?: (next: boolean) => void;
};

export function LoginCard({
  elevated,
  email,
  onEmailChange,
  password,
  onPasswordChange,
  onPasswordClipboard,
  rememberEmail,
  onRememberEmailChange,
  rememberPassword,
  onRememberPasswordChange,
  pending,
  onSubmit,
  onResetRequest,
  notices,
  designArt = false,
  onDesignArtChange,
}: LoginCardProps) {
  const passwordRef = useRef<HTMLInputElement>(null);

  return (
    <div
      style={{
        width: '100%',
        maxWidth: CARD_WIDTH,
        background: '#ffffff',
        borderRadius: '20px',
        padding: '32px 28px',
        /* 어두운 바탕 위에서는 옅은 테두리가 사라지고 그림자도 안 보인다 — 카드가 «떠 있는
           판» 이 아니라 «잘린 구멍» 으로 읽힌다. 시안이 설 때는 그림자를 깊게 준다. */
        border: elevated ? 'none' : '1px solid #e2e8f0',
        boxShadow: elevated ? '0 24px 64px rgba(0, 0, 0, 0.45)' : '0 12px 32px rgba(15, 23, 42, 0.08)',
      }}
    >
      {/* 상표는 글자가 아니라 CI 다(DOC-51 §6). 이름이 CI 안에 들어 있어 제목을 따로 찍지 않는다 —
          찍으면 `BoMI Lounge` 밑에 `Lounge` 가 한 번 더 붙는다. */}
      {/* 「디자인아트」 스위치는 카드 오른쪽 끝(사용자 지시 2026-10-20 「로고에 너무 붙어 있다」). 로고는 가운데에
          그대로 둔다 — 세 칸 격자(1fr auto 1fr)라 스위치가 제 칸을 가져, 절대 위치로 띄울 때처럼 좁은 카드에서
          로고와 겹치지 않는다. */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: '1fr auto 1fr',
          alignItems: 'center',
          columnGap: '12px',
          marginBottom: '24px',
        }}
      >
        {/* CI 를 상자로 감싸지 않는다(사용자 지시 2026-10-08 「CI 주변 테두리 없애」). */}
        <div style={{ gridColumn: 2, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
          <BrandMark height={34} />
        </div>
        {onDesignArtChange && (
          <label
            style={{
              gridColumn: 3,
              justifySelf: 'end',
              /* 스위치 밑에 글자가 붙어 묶음째 가운데에 서면 스위치가 로고보다 위로 뜬다 — 글자 한 줄의 절반만큼
                 내려 스위치를 로고 높이에 맞춘다(사용자 지시 2026-10-20). 자리를 차지하지 않게 transform 으로. */
              transform: 'translateY(10px)',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: '4px',
              fontSize: '11px',
              fontWeight: 600,
              color: '#64748b',
              cursor: 'pointer',
              flexShrink: 0,
            }}
          >
            {/* 이름은 「미디어아트」(사용자 지시 2026-10-21). 코드의 designArt 는 그대로 둔다 — 저장 칸 이름이다. */}
            <Switch checked={designArt} onChange={onDesignArtChange} label="미디어아트" />
            미디어아트
          </label>
        )}
      </div>

      <form onSubmit={onSubmit} autoComplete="on" style={{ display: 'grid', gap: '12px' }}>
        <input
          className="input-field"
          name="username"
          value={email}
          onChange={(event) => onEmailChange(event.target.value)}
          placeholder="아이디"
          autoComplete="username"
          /*
            아이디 칸의 엔터는 비밀번호 칸으로 넘긴다(앱 로그인과 같다). 비밀번호가 비어 있으면
            [로그인] 이 disabled 라 폼의 기본 엔터 제출이 아무 일도 안 하고 멈춰 있었다.
            한글 조합 중의 엔터는 글자를 확정하는 것이라 건드리지 않는다.
          */
          onKeyDown={(event) => {
            if (event.key !== 'Enter' || event.nativeEvent.isComposing) return;
            event.preventDefault();
            passwordRef.current?.focus();
          }}
        />
        <input
          ref={passwordRef}
          className="input-field"
          name="current-password"
          type="password"
          value={password}
          onChange={(event) => onPasswordChange(event.target.value)}
          onCopy={onPasswordClipboard}
          onCut={onPasswordClipboard}
          placeholder="비밀번호"
          autoComplete="current-password"
        />
        <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', color: '#4b5563' }}>
          <input
            type="checkbox"
            checked={rememberEmail}
            onChange={(event) => onRememberEmailChange(event.target.checked)}
          />
          아이디 저장
        </label>
        <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', color: '#4b5563' }}>
          <input
            type="checkbox"
            checked={rememberPassword}
            onChange={(event) => onRememberPasswordChange(event.target.checked)}
          />
          비밀번호 저장
        </label>
        <button
          type="submit"
          className="btn-primary"
          style={{ width: '100%', justifyContent: 'center' }}
          disabled={!email.trim() || !password.trim() || pending}
        >
          {pending ? '로그인 중...' : '로그인'}
        </button>
        <button
          type="button"
          className="btn-secondary"
          style={{ width: '100%', justifyContent: 'center' }}
          onClick={onResetRequest}
        >
          비밀번호 찾기
        </button>
      </form>

      {notices && <div style={{ marginTop: '14px', display: 'grid', gap: '8px' }}>{notices}</div>}
    </div>
  );
}

/**
 * 미리보기에 서는 카드. 같은 조각이되 <b>아무것도 받지 않는다</b>.
 *
 * <p>칸을 {@code disabled} 로 잠그지 않는다 — 그러면 회색으로 흐려져 <b>실제와 다른 그림</b>이
 * 되고, 정작 보려던 「이 바탕 위에서 칸이 읽히는가」 를 못 본다. 대신 층 전체가 손가락을 받지
 * 않게 하고 보조기기에서도 숨긴다(진짜 로그인 칸이 아니다).
 */
export function LoginCardPreview({ elevated }: { elevated: boolean }) {
  const ignore = () => {};
  return (
    <div style={{ pointerEvents: 'none', width: '100%', maxWidth: CARD_WIDTH }} aria-hidden>
      <LoginCard
        elevated={elevated}
        email=""
        onEmailChange={ignore}
        password=""
        onPasswordChange={ignore}
        onPasswordClipboard={ignore}
        rememberEmail
        onRememberEmailChange={ignore}
        rememberPassword
        onRememberPasswordChange={ignore}
        pending={false}
        onSubmit={(event) => event.preventDefault()}
        onResetRequest={ignore}
      />
    </div>
  );
}
